import React, { Fragment, useMemo, useState } from 'react';
import set from 'lodash/fp/set';
import sortBy from 'lodash/fp/sortBy';
import isEmpty from 'lodash/fp/isEmpty';
import format from 'date-fns/format';
import styled from 'styled-components';

import Button from '@/se/components/Button[DEPRECATED]';
import Spinner from '@/se/components/Spinner';
import { BaseError as Warning } from '@/se/components/errors/Error';
import { withTheme } from '@/se/theme';

import Checkbox from '../CheckboxInput';

const Dates = styled.div`
  margin-bottom: 1.5rem;
`;

const Date = styled.div`
  ${props => (props.selected ? 'background-color: rgba(255,255,255,0.25);' : '')}

  padding: 0.5rem;
  border-radius: 0.25rem;
  cursor: pointer;

  margin-bottom: 0.5rem;

  display: flex;
  align-items: center;

  > div {
    margin-right: 1rem;
    pointer-events: none;
    user-select: none;
  }

  > div:first-child {
    margin-bottom: 0;
    > input + label:before {
      margin-right: 0;
    }
  }
`;

// Visually flags rows whose date came back in the dry-run's staleDates list.
const StaleDate = styled(Date)`
  border-left: 0.25rem solid ${withTheme(theme => theme.warningColor.string())};
`;

const StaleLabel = styled.span`
  color: ${withTheme(theme => theme.warningTextColor.string())};
  font-size: ${withTheme(theme => theme.textSize.xs)};
  margin-left: auto;
  padding-left: 1rem;
`;

const StaleBanner = styled(Warning)`
  margin-bottom: 1.5rem;
`;

// Calls out that confirming a zero-count covered day is destructive (it cancels the day's
// remaining schedule), rather than letting it read like an unremarkable empty row.
const ZeroCountNote = styled.span`
  color: ${withTheme(theme => theme.warningTextColor.string())};
`;

const DEFAULT_STALE_WARNING =
  'Some dates fall outside the normal import window. They were left unselected below - review before including them.';

// Every date defaults to selected, except: dates the dry-run flagged as stale (the SQS ingest
// channel auto-skips those, but a manual backfill is a deliberate human decision, so leave them
// selectable but off by default), and zero-count covered days (confirming one cancels every
// remaining procedure on that day - a destructive action a checkbox the user may not read should
// never default to; the operator must explicitly opt in).
export const computeInitialDateSelection = (proceduresToImportByDate, staleDates) => {
  const staleSet = new Set(staleDates || []);

  return Object.entries(proceduresToImportByDate || {}).reduce((acc, [date, count]) => {
    acc[date] = !staleSet.has(date) && count > 0;
    return acc;
  }, {});
};

export const ScheduleUploadConfirmation = ({
  filePicker,
  file,
  proceduresToImport,
  proceduresToImportByDate,
  staleDates = [],
  coveredDates = [],
  staleWarning,
  onSubmit,
  onCancel,
  isUploading = false,
}) => {
  const staleSet = useMemo(() => new Set(staleDates), [staleDates]);

  const initialDates = useMemo(
    () => computeInitialDateSelection(proceduresToImportByDate, staleDates),
    [proceduresToImportByDate, staleDates]
  );

  const [dates, setDates] = useState(initialDates);

  const entries = sortBy(([date]) => date)(Object.entries(proceduresToImportByDate));

  const onConfirm = () => {
    const result = Object.entries(dates)
      .filter(([date, selected]) => selected)
      .map(([date]) => date);

    const urlParams = `&dates=${encodeURIComponent(JSON.stringify(result))}`;
    onSubmit(result.sort().reverse()[0], urlParams);
  };

  return isEmpty(entries) && isEmpty(coveredDates) ? (
    <Fragment>
      <p style={{ marginBottom: '1.5rem' }}>No procedures found.</p>
      {filePicker('Change File')}
    </Fragment>
  ) : (
    <Fragment>
      <p style={{ marginBottom: '1.5rem' }}>
        {proceduresToImport} procedure{proceduresToImport === 1 ? '' : 's'} found.
      </p>
      <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
        Note: Procedures will be processed in the background and may take a few moments to appear on the schedule.
      </p>
      {staleSet.size > 0 && <StaleBanner>{staleWarning || DEFAULT_STALE_WARNING}</StaleBanner>}
      <Dates>
        {entries.map(([date, count]) => {
          const isStale = staleSet.has(date);
          const DateRow = isStale ? StaleDate : Date;

          return (
            <DateRow key={date} selected={dates[date]} onClick={() => setDates(set(date, !dates[date])(dates))}>
              <Checkbox value={dates[date]} />
              <div>{format(new window.Date(date + 'T00:00:00'), 'MMMM D, YYYY')}</div>
              <div>
                {count > 0 ? (
                  `${count} procedure${count === 1 ? '' : 's'}.`
                ) : (
                  <ZeroCountNote>No procedures — confirming cancels this day&apos;s remaining schedule.</ZeroCountNote>
                )}
              </div>
              {isStale && <StaleLabel>Stale</StaleLabel>}
            </DateRow>
          );
        })}
      </Dates>
      <div>
        <Button
          disabled={isUploading || Object.entries(dates).filter(([_, selected]) => selected).length === 0}
          primary
          style={{ marginRight: '1rem' }}
          onClick={onConfirm}
        >
          {isUploading ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Spinner size="1rem" />
              Uploading...
            </span>
          ) : (
            'Complete'
          )}
        </Button>
        <Button disabled={isUploading} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Fragment>
  );
};
